import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import Footer from "../../../../footer";
import Header from "../../../../header/header";
import Navvbar from "../../../../header/Navvbar";
import Swal from "sweetalert2";
import "./addContactButton.css";

function AddContactButton() {
  const { accountId } = useParams();
  const trimmedAccountId = accountId ? accountId.trim() : "";
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [account, setAccount] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    middleInitial: "",
    lastName: "",
    role: "Dental Hygienist",
    accountId: trimmedAccountId,
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
  });

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        if (!trimmedAccountId) {
          throw new Error("Account ID is missing or empty");
        }

        const response = await fetch(
          `https://hms.backend.hiragroupcloud.com/api/account/account/${trimmedAccountId}`
        );

        const data = await response.json();

        if (response.ok) {
          setAccount(data);
        } else {
          throw new Error(data.message || "Failed to fetch account");
        }
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: `Failed to load account data: ${error.message}`,
          icon: "error",
          confirmButtonColor: "#004aad",
        }).then(() => {
          navigate("/accounts");
        });
      }
    };

    if (trimmedAccountId) {
      fetchAccount();
    } else {
      Swal.fire({
        title: "Invalid Request",
        text: "No account ID provided",
        icon: "warning",
        confirmButtonColor: "#004aad",
      }).then(() => {
        navigate("/accounts");
      });
    }
  }, [trimmedAccountId, navigate]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      accountId: trimmedAccountId,
    }));
  }, [trimmedAccountId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleSaveClick = async () => {
    if (!trimmedAccountId) {
      Swal.fire({
        title: "Missing Account",
        text: "Account ID is missing",
        icon: "error",
        confirmButtonColor: "#004aad",
      });

      navigate("/accounts");
      return;
    }

    const newErrors = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
    };

    setErrors(newErrors);

    if (newErrors.firstName || newErrors.lastName) {
      Swal.fire({
        title: "Validation Error",
        text: "First Name and Last Name are required!",
        icon: "warning",
        confirmButtonColor: "#004aad",
      });

      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `https://hms.backend.hiragroupcloud.com/api/contacts/${trimmedAccountId}/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            accountId: trimmedAccountId,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save contact");
      }

      if (!account) {
        throw new Error("Account data is not available");
      }

      Swal.fire({
        title: "Success!",
        text: "Contact saved successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        navigate(`/TabsWorking/${trimmedAccountId}`, {
          state: {
            accountData: account,
          },
        });
      });
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text:
          error.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#004aad",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!account && trimmedAccountId) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="add-contact-loading">
          Loading account data...
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <Navvbar />

      <div className="add-contact-page">

        <div className="add-contact-layout">

          {/* ================= SIDEBAR ================= */}

          <aside className="settings-sidebar">

            <h2 className="settings-title">
              Settings
            </h2>

            <nav className="settings-navigation">

              <a href="/Tabs" className="settings-link">
                Lab Details
              </a>

              <a href="/" className="settings-link">
                Store
              </a>

              <a href="/usersetting" className="settings-link">
                Users
              </a>

              <a href="/" className="settings-link">
                Permission Groups
              </a>

              <a href="/Account" className="settings-link">
                Accounts
              </a>

              <a href="/Owerviews" className="settings-link">
                Finances
              </a>

              <a href="/owersetting" className="settings-link">
                Case Settings
              </a>

            </nav>

          </aside>

          {/* ================= MAIN CONTENT ================= */}

          <main className="add-contact-content">

            <h3 className="page-title">
              Add Account Contact
            </h3>

            <h4 className="provider-title">
              Provider Details
            </h4>

            <div className="provider-card">

              <div className="contact-form">

                {/* Title */}

                <div className="form-group">

                  <label htmlFor="title">
                    Title
                  </label>

                  <input
                    type="text"
                    name="title"
                    id="title"
                    value={formData.title}
                    onChange={handleInputChange}
                  />

                </div>

                {/* First Name */}

                <div className="form-group">

                  <label htmlFor="firstName">
                    First Name <span>*</span>
                  </label>

                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={
                      errors.firstName ? "has-error" : ""
                    }
                  />

                  {errors.firstName && (
                    <div className="error-message">
                      First name is required
                    </div>
                  )}

                </div>

                {/* Middle Initial */}

                <div className="form-group">

                  <label htmlFor="middleInitial">
                    Middle Initial
                  </label>

                  <input
                    type="text"
                    name="middleInitial"
                    id="middleInitial"
                    value={formData.middleInitial}
                    onChange={handleInputChange}
                  />

                </div>

                {/* Last Name */}

                <div className="form-group">

                  <label htmlFor="lastName">
                    Last Name <span>*</span>
                  </label>

                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={
                      errors.lastName ? "has-error" : ""
                    }
                  />

                  {errors.lastName && (
                    <div className="error-message">
                      Last name is required
                    </div>
                  )}

                </div>

                {/* Role */}

                <div className="form-group">

                  <label htmlFor="role">
                    Role
                  </label>

                  <select
                    name="role"
                    id="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >

                    <option value="Dental Hygienist">
                      Dental Hygienist
                    </option>

                    <option value="Dental Provider">
                      Dental Provider
                    </option>

                    <option value="Dental Student Provider">
                      Dental Student Provider
                    </option>

                    <option value="Dental Faculty Provider">
                      Dental Faculty Provider
                    </option>

                    <option value="Dental Assistant">
                      Dental Assistant
                    </option>

                    <option value="Clinic Director">
                      Clinic Director
                    </option>

                    <option value="Office Manager">
                      Office Manager
                    </option>

                    <option value="Front Desk">
                      Front Desk
                    </option>

                    <option value="BookKeeper">
                      BookKeeper
                    </option>

                    <option value="Practice Mini-Lab Technician">
                      Practice Mini-Lab Technician
                    </option>

                    <option value="Administrator">
                      Administrator
                    </option>

                  </select>

                </div>

                {/* Save */}

                <div className="save-button-wrapper">

                  <Button
                    onClick={handleSaveClick}
                    disabled={isLoading}
                    className="save-button"
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </Button>

                </div>

              </div>

            </div>

          </main>

        </div>

      </div>

      <div className="footer-container">
        <Footer />
      </div>
    </>
  );
}

export default AddContactButton;